import { useContext, useEffect, useState } from "react";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";

export interface WhatsappDerivedState {
  isRequired: boolean;
  isNotCreated: boolean;
  isDisconnected: boolean;
  isConnected: boolean;
  gracePeriodMinutes: number | null;
  isGracePeriodExpired: boolean;
  isBlocked: boolean;
  isWarning: boolean;
  needsFirstTimeSetup: boolean;
}

// Returns null when gracePeriodEndedAt is null (unlimited — never expires).
const computeGracePeriodMinutes = (
  gracePeriodEndedAt: string | null,
): number | null => {
  if (!gracePeriodEndedAt) return null;
  return Math.max(
    0,
    Math.ceil((new Date(gracePeriodEndedAt).getTime() - Date.now()) / 60000),
  );
};

const useWhatsappStatus = (): WhatsappDerivedState => {
  const { whatsappInstance } = useContext(NawyPassportContext);

  const [gracePeriodMinutes, setGracePeriodMinutes] = useState<number | null>(
    () =>
      computeGracePeriodMinutes(whatsappInstance?.gracePeriodEndedAt ?? null),
  );

  useEffect(() => {
    setGracePeriodMinutes(
      computeGracePeriodMinutes(whatsappInstance?.gracePeriodEndedAt ?? null),
    );

    const interval = setInterval(() => {
      setGracePeriodMinutes(
        computeGracePeriodMinutes(whatsappInstance?.gracePeriodEndedAt ?? null),
      );
    }, 60_000);

    return () => clearInterval(interval);
  }, [whatsappInstance?.gracePeriodEndedAt]);

  const isRequired = whatsappInstance?.required === true;
  const isNotCreated = isRequired && whatsappInstance?.status === "not-created";

  const isDisconnected = isRequired && whatsappInstance?.status !== "open";

  const isConnected =
    isRequired &&
    whatsappInstance?.lastConnectedAt != null &&
    (!whatsappInstance?.lastDisconnectedAt ||
      new Date(whatsappInstance.lastConnectedAt) >=
        new Date(whatsappInstance.lastDisconnectedAt));

  // null = unlimited grace period (never blocked); 0 = expired; >0 = minutes remaining
  const isGracePeriodExpired =
    gracePeriodMinutes !== null && gracePeriodMinutes <= 0;
  const isBlocked = isDisconnected && isGracePeriodExpired;
  const isWarning = isDisconnected && !isGracePeriodExpired;

  return {
    isRequired,
    isNotCreated,
    isDisconnected,
    isConnected,
    gracePeriodMinutes,
    isGracePeriodExpired,
    isBlocked,
    isWarning,
    needsFirstTimeSetup: isNotCreated,
  };
};

export default useWhatsappStatus;
